import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useState } from "react";

/**
 * 차트2의 행렬·구간·맞춤. 밀도는 회색이고, 막힘과 한계만 색이다.
 */

const LEVEL = [
  tokens.colorNeutralBackground4,
  tokens.colorNeutralStroke2,
  tokens.colorNeutralStroke1,
  tokens.colorNeutralForeground4,
  tokens.colorNeutralForeground3,
];

export interface MatrixCell {
  level: number;
  ranOut?: boolean;
  label: string;
}

export interface MatrixDay {
  key: string;
  week?: string;
  letter: string;
  today?: boolean;
  locked?: boolean;
  hours: MatrixCell[];
}

export interface WindowSegment {
  key: string;
  level: number;
  flex: number;
  hit?: boolean;
  current?: boolean;
}

export interface WindowRow {
  key: string;
  name: string;
  segments: WindowSegment[];
}

export interface FitWindow {
  key: string;
  flex: number;
  level: number;
  hit?: boolean;
  current?: boolean;
  label: string;
}

export interface FitPeak {
  key: string;
  label: string;
  value: number;
  hit?: boolean;
  now?: boolean;
}

export interface FitDetail {
  average: string;
  atLimit: string;
  locked: string;
  unused: string;
  note: string;
  averageValue: number;
  peaks: FitPeak[];
}

export interface FitRow {
  key: string;
  name: string;
  windows: FitWindow[];
  verdict: "tight" | "fit" | "spare";
  detail: FitDetail;
}

const VERDICT: Record<FitRow["verdict"], string> = {
  tight: "좁음",
  fit: "맞음",
  spare: "여유",
};

export function MatrixBoard({
  title,
  note,
  hours,
  days,
  weekday,
  weekend,
  marks,
  windows,
}: {
  title: string;
  note: string;
  hours: string[];
  days: MatrixDay[];
  weekday: number[];
  weekend: number[];
  marks: boolean[];
  windows: WindowRow[];
}) {
  const styles = useStyles();
  const columns = `repeat(${days.length}, minmax(0, 1fr))`;
  return (
    <div className={styles.board}>
      <p className={styles.headline}>{title}</p>
      <Caption1 className={styles.quiet}>{note}</Caption1>
      <div className={styles.matrix} style={{ gridTemplateRows: `auto auto repeat(${hours.length}, 14px) 8px` }}>
        <div className={styles.cols} style={{ gridColumn: 2, gridRow: 1, gridTemplateColumns: columns }}>
          {days.map((day) => (
            <span key={`${day.key}-week`} className={styles.week}>
              {day.week ?? ""}
            </span>
          ))}
        </div>
        <Caption1 className={styles.profileLabel}>하루 평균</Caption1>
        <div className={styles.cols} style={{ gridColumn: 2, gridRow: 2, gridTemplateColumns: columns }}>
          {days.map((day) => (
            <span key={`${day.key}-letter`} className={mergeClasses(styles.letter, day.today && styles.todayLetter)}>
              {day.letter}
            </span>
          ))}
        </div>
        {hours.map((hour, index) => (
          <HourRow key={hour} hour={hour} index={index} days={days} columns={columns} />
        ))}
        <div className={styles.profile} style={{ gridColumn: 3, gridRow: `3 / span ${hours.length}` }}>
          <DayProfile weekday={weekday} weekend={weekend} marks={marks} />
        </div>
        <Caption1 className={styles.hour} style={{ gridColumn: 1, gridRow: hours.length + 3 }}>
          잠김
        </Caption1>
        <div className={styles.cols} style={{ gridColumn: 2, gridRow: hours.length + 3, gridTemplateColumns: columns }}>
          {days.map((day) => (
            <span key={`${day.key}-lock`} className={mergeClasses(styles.lock, day.locked && styles.lockOn)} title={day.locked ? `${day.letter} 잠김` : undefined} />
          ))}
        </div>
      </div>
      <div className={styles.legend}>
        <Swatch label="없음" color={tokens.colorNeutralBackground3} />
        <Swatch label="낮음" color={LEVEL[1] ?? ""} />
        <Swatch label="보통" color={LEVEL[2] ?? ""} />
        <Swatch label="높음" color={LEVEL[3] ?? ""} />
        <Swatch label="가득" color={LEVEL[4] ?? ""} />
        <Swatch label="잠김" color={tokens.colorPaletteRedBackground3} />
        <Swatch label="막힘" color={tokens.colorPaletteRedForeground1} round />
        <Swatch label="평일" color={tokens.colorNeutralForeground2} line />
        <Swatch label="주말" color={tokens.colorNeutralForeground3} dashed />
      </div>
      <div className={styles.windowHead}>
        <p className={styles.subhead}>구간별 사용</p>
        <Caption1 className={styles.quiet}>각 구간이 얼마나 찼는지. 점선은 이번 구간입니다.</Caption1>
      </div>
      <div className={styles.windows}>
        {windows.map((row) => (
          <div key={row.key} className={styles.windowRow}>
            <div className={styles.track} aria-label={row.name}>
              {row.segments.map((segment) => (
                <span
                  key={segment.key}
                  className={mergeClasses(styles.seg, segment.current && styles.current)}
                  style={{ flex: segment.flex, backgroundColor: segment.hit ? tokens.colorPaletteRedBackground3 : LEVEL[Math.max(0, Math.min(4, segment.level))] }}
                />
              ))}
            </div>
            <span className={styles.windowName}>{row.name}</span>
          </div>
        ))}
      </div>
      <div className={styles.legend}>
        <Swatch label="40% 미만" color={LEVEL[0] ?? ""} />
        <Swatch label="40–60%" color={LEVEL[1] ?? ""} />
        <Swatch label="60–80%" color={LEVEL[2] ?? ""} />
        <Swatch label="80–99%" color={LEVEL[3] ?? ""} />
        <Swatch label="한계" color={tokens.colorPaletteRedBackground3} />
        <Swatch label="이번 구간" color={tokens.colorNeutralBackground1} dashed />
      </div>
    </div>
  );
}

function HourRow({ hour, index, days, columns }: { hour: string; index: number; days: MatrixDay[]; columns: string }) {
  const styles = useStyles();
  const row = index + 3;
  return (
    <>
      <Caption1 className={styles.hour} style={{ gridColumn: 1, gridRow: row }}>
        {hour}
      </Caption1>
      <div className={styles.cols} style={{ gridColumn: 2, gridRow: row, gridTemplateColumns: columns }}>
        {days.map((day) => {
          const cell = day.hours[index];
          const level = cell?.level ?? -1;
          return (
            <span
              key={`${day.key}-${hour}`}
              className={mergeClasses(styles.cell, day.today && styles.todayCell)}
              style={level < 0 ? undefined : { backgroundColor: LEVEL[Math.max(0, Math.min(4, level))] }}
              title={cell?.label}
              aria-label={cell?.label}
            >
              {cell?.ranOut ? <span className={styles.dot} /> : null}
            </span>
          );
        })}
      </div>
    </>
  );
}

function DayProfile({ weekday, weekend, marks }: { weekday: number[]; weekend: number[]; marks: boolean[] }) {
  const count = Math.max(weekday.length, 1);
  const width = 72;
  const height = 100;
  const x = (value: number) => 4 + (Math.max(0, Math.min(4, value)) / 4) * (width - 18);
  const y = (index: number) => ((index + 0.5) / count) * height;
  const line = (values: number[]) => values.map((value, index) => `${x(value)},${y(index)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" role="img" aria-label="시간대별 하루 평균">
      <polyline points={line(weekend)} fill="none" stroke={tokens.colorNeutralForeground3} strokeWidth="1.5" strokeDasharray="3 2" />
      <polyline points={line(weekday)} fill="none" stroke={tokens.colorNeutralForeground2} strokeWidth="1.5" />
      {marks.map((mark, index) =>
        mark ? <line key={index} x1={width - 8} x2={width - 2} y1={y(index)} y2={y(index)} stroke={tokens.colorPaletteRedForeground1} strokeWidth="2" /> : null,
      )}
    </svg>
  );
}

function Swatch({ label, color, round, line, dashed }: { label: string; color: string; round?: boolean; line?: boolean; dashed?: boolean }) {
  const styles = useStyles();
  return (
    <span className={styles.legendItem}>
      <span
        className={mergeClasses(styles.swatch, round && styles.swatchRound, (line || dashed) && styles.swatchLine)}
        style={line || dashed ? { borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}` } : { backgroundColor: color }}
      />
      <Caption1 className={styles.quiet}>{label}</Caption1>
    </span>
  );
}

export function FitBoard({ headline, sub, rows }: { headline: string; sub: string; rows: FitRow[] }) {
  const styles = useStyles();
  const [selected, setSelected] = useState(rows[0]?.key ?? "");
  const current = rows.find((row) => row.key === selected) ?? rows[0];
  return (
    <div className={styles.board}>
      <p className={styles.headline}>{headline}</p>
      <Caption1 className={styles.quiet}>{sub}</Caption1>
      <div className={styles.fit}>
        <div role="listbox" aria-label="단계 맞춤" className={styles.fitList}>
          {rows.map((row) => (
            <button key={row.key} type="button" role="option" aria-selected={current?.key === row.key} className={mergeClasses(styles.fitRow, current?.key === row.key && styles.fitOn)} onClick={() => setSelected(row.key)}>
              <span className={styles.fitName}>{row.name}</span>
              <span className={styles.wins}>
                {row.windows.map((window) => (
                  <span
                    key={window.key}
                    className={mergeClasses(styles.win, window.current && styles.current)}
                    style={{ flex: window.flex, backgroundColor: window.hit ? tokens.colorPaletteRedBackground3 : LEVEL[Math.max(0, Math.min(4, window.level))] }}
                    title={window.label}
                  />
                ))}
              </span>
              <span className={mergeClasses(styles.verdict, row.verdict === "tight" && styles.tight, row.verdict === "spare" && styles.spare)}>{VERDICT[row.verdict]}</span>
            </button>
          ))}
          <Caption1 className={styles.quiet}>칸의 너비는 구간 길이입니다. 빨간 칸은 한계, 점선은 이번 구간입니다.</Caption1>
        </div>
        {current ? <FitDetailView row={current} /> : null}
      </div>
    </div>
  );
}

function FitDetailView({ row }: { row: FitRow }) {
  const styles = useStyles();
  const { detail } = row;
  return (
    <div className={styles.detail}>
      <div className={styles.detailHead}>
        <p className={styles.sideTitle}>{row.name}</p>
        <span className={mergeClasses(styles.verdict, row.verdict === "tight" && styles.tight, row.verdict === "spare" && styles.spare)}>{VERDICT[row.verdict]}</span>
      </div>
      <div className={styles.facts}>
        <Fact label="평균 정점" value={detail.average} />
        <Fact label="한계" value={detail.atLimit} />
        <Fact label="잠김" value={detail.locked} />
        <Fact label="미사용" value={detail.unused} />
      </div>
      <Caption1 className={styles.quiet}>주별 정점 · 최근 13주</Caption1>
      <div className={styles.peaks} style={{ gridTemplateColumns: `repeat(${detail.peaks.length}, minmax(0, 1fr))` }} aria-hidden>
        <span className={styles.limit} />
        <span className={styles.average} style={{ bottom: `${detail.averageValue}%` }} />
        {detail.peaks.map((peak) => (
          <span key={peak.key} className={styles.peakCol}>
            <span className={mergeClasses(styles.peakBar, peak.hit && styles.peakHit, peak.now && styles.peakNow)} style={{ height: `${Math.max(0, Math.min(100, peak.value))}%` }} />
          </span>
        ))}
      </div>
      <div className={styles.peakLabels} style={{ gridTemplateColumns: `repeat(${detail.peaks.length}, minmax(0, 1fr))` }}>
        {detail.peaks.map((peak) => (
          <Caption1 key={peak.key} className={styles.axis}>
            {peak.label}
          </Caption1>
        ))}
      </div>
      <Caption1 className={styles.quiet}>{detail.note}</Caption1>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  return (
    <span className={styles.fact}>
      <Caption1 className={styles.quiet}>{label}</Caption1>
      <span className={styles.factValue}>{value}</span>
    </span>
  );
}

const useStyles = makeStyles({
  board: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  headline: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  subhead: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  matrix: {
    display: "grid",
    gridTemplateColumns: "36px minmax(0, 1fr) 88px",
    columnGap: "8px",
    rowGap: "2px",
    alignItems: "center",
  },
  cols: {
    display: "grid",
    gap: "1px",
    minWidth: 0,
  },
  week: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    color: tokens.colorNeutralForeground2,
    whiteSpace: "nowrap",
    overflow: "visible",
  },
  letter: {
    textAlign: "center",
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    color: tokens.colorNeutralForeground3,
  },
  todayLetter: {
    color: tokens.colorNeutralForeground1,
  },
  hour: {
    color: tokens.colorNeutralForeground3,
    textAlign: "right",
  },
  cell: {
    position: "relative",
    height: "14px",
    borderRadius: "1px",
    backgroundColor: tokens.colorNeutralBackground3,
    minWidth: 0,
  },
  todayCell: {
    boxShadow: `inset 0 0 0 1px ${tokens.colorNeutralStrokeAccessible}`,
  },
  dot: {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: "5px",
    height: "5px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorPaletteRedForeground1,
    transform: "translate(-50%, -50%)",
  },
  lock: {
    height: "6px",
    borderRadius: "1px",
    backgroundColor: tokens.colorNeutralBackground4,
  },
  lockOn: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  profile: {
    height: "100%",
    minHeight: 0,
  },
  profileLabel: {
    gridColumn: 3,
    gridRow: 1,
    color: tokens.colorNeutralForeground3,
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px 12px",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  swatch: {
    width: "12px",
    height: "12px",
    borderRadius: "2px",
    flexShrink: 0,
  },
  swatchRound: {
    width: "6px",
    height: "6px",
    borderRadius: tokens.borderRadiusCircular,
  },
  swatchLine: {
    height: 0,
    backgroundColor: "transparent",
  },
  windowHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    marginTop: "8px",
  },
  windows: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  windowRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 132px",
    gap: "10px",
    alignItems: "center",
  },
  track: {
    display: "flex",
    height: "16px",
    gap: "2px",
    minWidth: 0,
  },
  seg: {
    minWidth: 0,
    borderRadius: "2px",
  },
  current: {
    outline: `1px dashed ${tokens.colorNeutralStrokeAccessible}`,
    outlineOffset: "-1px",
  },
  windowName: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fit: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.45fr) minmax(240px, 0.8fr)",
    gap: "24px",
    alignItems: "start",
  },
  fitList: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  fitRow: {
    display: "grid",
    gridTemplateColumns: "108px minmax(0, 1fr) 40px",
    gap: "8px",
    alignItems: "center",
    width: "100%",
    padding: "4px 6px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: "inherit",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  fitOn: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  fitName: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  wins: {
    display: "flex",
    height: "16px",
    gap: "2px",
    minWidth: 0,
  },
  win: {
    minWidth: "4px",
    borderRadius: "2px",
  },
  verdict: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    textAlign: "right",
  },
  tight: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  spare: {
    color: tokens.colorNeutralForeground3,
  },
  detail: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  detailHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "8px",
  },
  sideTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  facts: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px 16px",
  },
  fact: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  factValue: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground1,
  },
  peaks: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "repeat(13, minmax(0, 1fr))",
    gap: "3px",
    height: "88px",
    alignItems: "end",
  },
  peakCol: {
    display: "flex",
    alignItems: "flex-end",
    height: "100%",
    minWidth: 0,
  },
  peakBar: {
    width: "100%",
    borderRadius: "2px",
    backgroundColor: tokens.colorNeutralForeground4,
  },
  peakHit: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  peakNow: {
    backgroundColor: "transparent",
    border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
    boxSizing: "border-box",
  },
  limit: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    borderTop: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
  },
  average: {
    position: "absolute",
    left: 0,
    right: 0,
    borderTop: `1px dashed ${tokens.colorNeutralForeground3}`,
  },
  peakLabels: {
    display: "grid",
    gridTemplateColumns: "repeat(13, minmax(0, 1fr))",
    gap: "3px",
  },
  axis: {
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    whiteSpace: "nowrap",
  },
});
